// Hook de Cloudflare Turnstile para formularios. Devuelve:
// - enabled: si Turnstile está activo (llaves configuradas)
// - token: token actual del widget invisible (null = sin config, "" = pendiente)
// - widget: elemento <TurnstileWidget/> para montar en el formulario
// - ensureToken(timeoutMs): espera y devuelve un token válido (o null si apagado)
// - reset: reinicia el widget (tras un submit fallido)
// - verify(): verifica el token contra el backend (para auth forms); {success}
//
// Formularios de pago: pasan `turnstile_token: await ts.ensureToken()` en el
// payload; el backend valida con assertTurnstile.
// Auth forms (backend de la plataforma): llaman `await ts.verify()` antes del
// SDK call (defense-in-depth vía verifyTurnstile).
import { useCallback, useEffect, useRef, useState, createElement, Fragment } from "react";
import { base44 } from "@/api/base44Client";
import TurnstileWidget from "@/components/turnstile/TurnstileWidget";
import TurnstileVerifyModal from "@/components/turnstile/TurnstileVerifyModal";

export function useTurnstile(action, { size = "invisible" } = {}) {
  const [enabled, setEnabled] = useState(false);
  const [token, setToken] = useState(null);
  const tokenRef = useRef(null);
  const [resetKey, setResetKey] = useState(0);
  // Modal de re-verificación visible: se abre cuando el widget invisible falla.
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const verifyResolverRef = useRef(null);
  const passRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    base44.functions.invoke("getTurnstileConfig")
      .then(res => { if (!cancelled) setEnabled(!!(res.data || res)?.enabled); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleToken = useCallback((t) => { tokenRef.current = t; setToken(t); }, []);

  const reset = useCallback(() => {
    tokenRef.current = null;
    setToken(null);
    setResetKey(k => k + 1);
  }, []);

  // Espera hasta timeoutMs un token válido (>=10 chars). Si Turnstile está
  // apagado, devuelve null inmediatamente (fail-open en el backend). Resetea
  // el widget tras entregar el token: los tokens de Turnstile son de un solo
  // uso, así la próxima llamada genera uno fresco.
  //
  // Si el widget ya disparó error/expiración (token=""), devuelve null inmediato
  // sin esperar el timeout completo — el widget no va a producir un token válido.
  // Esto evita una espera de 4s en cada intento de recarga cuando Turnstile no
  // puede verificar (iframe preview, dominio no whitelist, ad-blocker, red).
  // El backend hace fail-open para missing_token, así la recarga procede.
  const ensureToken = useCallback(async (timeoutMs = 4000) => {
    if (!enabled) return null;
    const start = Date.now();
    while (Date.now() - start < timeoutMs) {
      const t = tokenRef.current;
      if (t && t.length > 10) {
        tokenRef.current = null;
        setToken(null);
        setResetKey(k => k + 1);
        return t;
      }
      // El widget disparó error/expiración (callback onToken("")): no esperes.
      if (t === "") {
        tokenRef.current = null;
        setResetKey(k => k + 1);
        return null;
      }
      await new Promise(r => setTimeout(r, 150));
    }
    // Timeout sin respuesta del widget: resetea para que reintente en la próxima llamada.
    setResetKey(k => k + 1);
    return null;
  }, [enabled]);

  // Abre el modal con widget visible y devuelve una promesa que resuelve con
  // el token verificado por el usuario (o null si cierra sin verificar).
  const promptVisibleVerification = useCallback(() => {
    return new Promise((resolve) => {
      verifyResolverRef.current = resolve;
      setVerifyModalOpen(true);
    });
  }, []);

  const handleVisibleToken = useCallback((t) => {
    const resolver = verifyResolverRef.current;
    verifyResolverRef.current = null;
    setVerifyModalOpen(false);
    if (resolver) resolver(t && t.length > 10 ? t : null);
  }, []);

  const handleModalClose = useCallback(() => {
    const resolver = verifyResolverRef.current;
    verifyResolverRef.current = null;
    setVerifyModalOpen(false);
    if (resolver) resolver(null);
  }, []);

  // Verifica el token contra el backend verifyTurnstile (para auth forms).
  // Si el widget invisible no produce token, abre el modal con widget visible
  // para que el usuario reintente; al pasar la verificación (check verde) el
  // modal se cierra solo y se valida ese token contra el backend.
  const verify = useCallback(async () => {
    if (!enabled) return { success: true, skipped: true };
    let t = await ensureToken();
    if (!t) t = await promptVisibleVerification();
    if (!t) return { success: false, error: "no_token" };
    try {
      const res = await base44.functions.invoke("verifyTurnstile", { token: t });
      const out = res.data || res;
      // Pase firmado del servidor para el cotizador web (válido 10 min).
      if (out?.pass) passRef.current = out.pass;
      return out;
    } catch {
      return { success: false, error: "verify_failed" };
    }
  }, [enabled, ensureToken, promptVisibleVerification]);

  // El elemento `widget` incluye tanto el widget invisible (siempre montado en
  // el formulario) como el modal de re-verificación visible. Los formularios
  // ya renderizan {ts.widget}, así que heredan el modal sin cambios.
  const widget = createElement(
    Fragment,
    null,
    createElement(TurnstileWidget, { key: `inv-${resetKey}`, action, onToken: handleToken, size }),
    createElement(TurnstileVerifyModal, {
      key: `modal-${resetKey}`,
      open: verifyModalOpen,
      onClose: handleModalClose,
      onToken: handleVisibleToken,
      action,
    })
  );

  // Pase vigente del último verify() exitoso (null si Turnstile está apagado).
  const getPass = useCallback(() => passRef.current, []);

  return { enabled, token, widget, reset, ensureToken, verify, getPass };
}